"use client";

import { datadogRum } from "@datadog/browser-rum";
import { DatadogAppRouter } from "@datadog/browser-rum-nextjs";
import { useState } from "react";

import {
  RUM_ROUTE_GROUP_CONTEXT_KEY,
  type RumRouteGroup,
  shouldStartRumViews,
} from "@/lib/analytics/rum-session-sampling";

/**
 * Last group written to RUM's global context. Module scope so a remount that
 * lands on the same group skips the write, which would otherwise deep-copy the
 * whole global context on every navigation for no change.
 */
let taggedGroup: RumRouteGroup | undefined;

/**
 * Starts Datadog RUM views for one route group, subject to that group's sample
 * rate. Mount once per route-group layout: RUM is initialized in
 * instrumentation-client.ts, but `nextjsPlugin()` makes view tracking manual, so
 * a route group that never mounts this emits no RUM data at all.
 */
export function DatadogRumViews({
  routeGroup,
}: {
  routeGroup: RumRouteGroup;
}): React.ReactElement | null {
  // Both resolved once per mount. Sampling is sticky per browsing session, so
  // remounting on a cross-group navigation cannot flip a visitor's outcome; the
  // tag is set here rather than in an effect so it is in place before
  // <DatadogAppRouter /> below starts the view.
  //
  // Global (not view) context on purpose: it is read as each event is
  // assembled, so events carry the group the visitor is in at that moment,
  // including events from views this component did not create, such as session
  // renewal or a back/forward cache restore.
  const [startViews] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    if (taggedGroup !== routeGroup) {
      taggedGroup = routeGroup;
      datadogRum.setGlobalContextProperty(
        RUM_ROUTE_GROUP_CONTEXT_KEY,
        routeGroup,
      );
    }
    return shouldStartRumViews(routeGroup);
  });

  return startViews ? <DatadogAppRouter /> : null;
}
